import './styles.css';
import './styles/staleness.css';
import './styles/kiosk.css';
import './styles/cell.css';
import './styles/list.css';
import './styles/background.css';
// services/socket.js module load'da Socket.io auto-init eder; mock/live mode'da
// gereksiz WS denemesi yapar (eski tracer-bullet artefaktı). Sadece classic mode
// içinde lazy import edilir — bkz. bootClassicTile.
import { applyTheme, renderLayout, updateTick, type Layout, type Theme, tickStore } from './renderer/index.js';
import { setSymbolLabels } from './state/symbol-labels.js';
import { apiBase } from './assets.js';
import { bootPairMode } from './pair.js';
import './components/fp-service-overlay.js';
import './components/fp-connection-banner.js';
import './components/fp-staleness-banner.js';
import './components/fp-fatal-error.js';
import { markLayoutReady } from './state/screen-status.js';
import { startWatchdog } from './watchdog/watchdog-controller.js';
import { startMemoryMonitor } from './watchdog/memory-monitor.js';
import { installConsoleCapture } from './diagnostic/log-buffer.js';
import { installDiagnosticTrigger } from './diagnostic/diagnostic-trigger.js';
import { installErrorHandlers } from './telemetry/error-handlers.js';
import { startLogBatcher } from './telemetry/log-batcher.js';
import { initSentry, setDeviceContext, breadcrumb } from './sentry.js';

// Sentry EN BAŞTA (#43) — sonraki hatalar yakalanabilsin
initSentry();

// Diagnostic (#37) + telemetri (#38) — console yakalama + hata raporlama erken kurulur
installConsoleCapture();
installErrorHandlers();
installDiagnosticTrigger();

const app = document.getElementById('app');
if (!app) throw new Error('#app element bulunamadı');

// Bağlantı bandı her modda görünür (mock/preview'da connectionState online → gizli).
// Abonelik kesintisi örtüsü — bantlardan ÖNCE eklenir ama z-index'i daha yüksektir:
// kesinti varsa bağlantı/bayatlama uyarıları da altında kalmalı (tek net mesaj).
if (!document.querySelector('fp-service-overlay')) {
    document.body.appendChild(document.createElement('fp-service-overlay'));
}

if (!document.querySelector('fp-connection-banner')) {
    document.body.appendChild(document.createElement('fp-connection-banner'));
}
// Veri-durumu bandı (boş/önbellek/kritik) yalnız CANLI modda anlamlı — mock/preview'da
// canlı gateway doğrulaması olmadığı için yanlışlıkla "önbellek" uyarısı vermesin (K1/K3).
function mountStalenessBanner(): void {
    if (!document.querySelector('fp-staleness-banner')) {
        document.body.appendChild(document.createElement('fp-staleness-banner'));
    }
}

const params = new URLSearchParams(location.search);
const isMock = params.get('mock') === '1';
const isClassic = params.get('classic') === '1';
const isPair = location.pathname === '/pair' || params.get('pair') === '1';
const previewLayoutId = params.get('layout');
const previewTemplateId = params.get('template');
const previewToken = params.get('preview_token');

if (isPair) {
    // Eşleştirme sayfası (Faz 1.14) — cihaz token'ı edinme
    void bootPairMode(app);
} else if (previewTemplateId && previewToken) {
    // Op şablon editörü canlı önizleme (Task #60 Faz D) — şablon document + sistem teması, örnek fiyatlar
    void bootPreviewTemplate(previewTemplateId, previewToken);
} else if (previewLayoutId && previewToken) {
    // Editör canlı önizleme (Faz 1.10) — gerçek layout+theme, örnek fiyatlar
    void bootPreviewMode(previewLayoutId, previewToken);
} else if (isClassic) {
    // Geriye uyumluluk: önceki tracer-bullet fp-price-tile preview
    void bootClassicTile();
} else if (isMock) {
    void bootMockMode();
} else {
    void bootLiveMode();
}

/**
 * Mock mode — Gateway bağlantısız preview.
 * /mock/layout-classic-grid.json + /mock/theme-dark-gold.json okur,
 * 300ms aralıkla random tick püskürtür.
 * URL: http://localhost:5173?mock=1
 */
async function bootMockMode(): Promise<void> {
    // Mock modda bağlantı bandı görünmesin
    const { connectionState } = await import('./state/connection-state.js');
    connectionState.set('online');

    const layout = await fetchExample<Layout>('/mock/layout-classic-grid.json');
    const theme = await fetchExample<Theme>('/mock/theme-dark-gold.json');

    applyTheme(theme);
    renderLayout(layout, app!);
    simulateTicks(layout);

    (window as unknown as { __fpDebug: object }).__fpDebug = {
        tickStore,
        layout,
        theme,
        applyTheme,
        renderLayout,
    };

    console.info('[mock] layout+theme yüklendi, tick simulator aktif. window.__fpDebug ile inspect.');
}

/**
 * Örnek tick simülatörü — mock + preview modlarında gerçek feed yokken fiyat hareketi gösterir.
 */
function simulateTicks(layout: Layout): void {
    const symbols = new Set<string>();
    for (const page of layout.pages) {
        for (const comp of page.components) {
            if (comp.type === 'symbol_grid' || comp.type === 'symbol_list' || comp.type === 'symbol_ticker') {
                comp.symbols.forEach((s) => symbols.add(s));
            }
        }
    }
    const symbolList = [...symbols];
    const basePrices: Record<string, number> = {};
    symbolList.forEach((s) => {
        basePrices[s] = 1000 + Math.random() * 5000;
    });
    setInterval(() => {
        const s = symbolList[Math.floor(Math.random() * symbolList.length)];
        if (!s) return;
        const base = basePrices[s] ?? 1000;
        const newPrice = base + (Math.random() - 0.5) * (base * 0.002);
        basePrices[s] = newPrice;
        updateTick(s, {
            symbol: s,
            bid: newPrice.toFixed(2),
            ask: (newPrice * 1.003).toFixed(2),
            server_ts: Date.now(),
        });
    }, 300);
}

/**
 * Preview mode — editör canlı önizleme iframe'i (Faz 1.10).
 * `?layout={id}&preview_token=` → public preview endpoint'inden GERÇEK layout+theme çek,
 * render et, örnek fiyatlarla canlandır. (Eski davranış: token'sız mock'a düşüp sabit
 * classic-grid gösteriyordu — kullanıcının düzeni hiç görünmüyordu.)
 */
async function bootPreviewMode(layoutId: string, token: string): Promise<void> {
    const { connectionState } = await import('./state/connection-state.js');
    connectionState.set('online');
    try {
        const url = `${apiBase()}/api/public/layouts/${encodeURIComponent(layoutId)}/preview?token=${encodeURIComponent(token)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`preview HTTP ${res.status}`);
        const data = (await res.json()) as { layout: Layout; theme: Theme | null; labels?: Record<string, string> };
        // Sembol görünen adları render'dan ÖNCE (cell'ler label'ı okurken hazır olsun).
        setSymbolLabels(data.labels);
        if (data.theme) applyTheme(data.theme);
        renderLayout(data.layout, app!);
        simulateTicks(data.layout);
        console.info('[preview] gerçek layout + theme render edildi (örnek fiyatlar).');
    } catch (err) {
        console.error('[preview] önizleme yüklenemedi:', err);
        app!.innerHTML = '<div style="padding:2rem;color:#94a3b8;text-align:center;">Önizleme yüklenemedi. Düzeni kaydedip tekrar deneyin.</div>';
    }
}

/**
 * Şablon önizleme modu (Task #60 Faz D) — op şablon editörü iframe'i.
 * `?template={id}&preview_token=` → public template-preview endpoint'inden şablon
 * document + sistem varsayılan teması çek, render et, örnek fiyatlarla canlandır.
 * Tenant-context yok (şablon tenant'a ait değil).
 */
async function bootPreviewTemplate(templateId: string, token: string): Promise<void> {
    const { connectionState } = await import('./state/connection-state.js');
    connectionState.set('online');
    try {
        const url = `${apiBase()}/api/public/templates/${encodeURIComponent(templateId)}/preview?token=${encodeURIComponent(token)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`template preview HTTP ${res.status}`);
        const data = (await res.json()) as { layout: Layout; theme: Theme | null; labels?: Record<string, string> };
        // Şablon önizlemede global katalog adları — kod yerine "Gram Altın" vb.
        setSymbolLabels(data.labels);
        if (data.theme) applyTheme(data.theme);
        renderLayout(data.layout, app!);
        simulateTicks(data.layout);
        console.info('[preview:template] şablon + sistem teması render edildi (örnek fiyatlar).');
    } catch (err) {
        console.error('[preview:template] önizleme yüklenemedi:', err);
        app!.innerHTML = '<div style="padding:2rem;color:#94a3b8;text-align:center;">Önizleme yüklenemedi. Şablonu kaydedip tekrar deneyin.</div>';
    }
}

/**
 * Live mode — Gateway initial_state protokolü (#31).
 * Device token localStorage'dan; Gateway bağlanır, initial_state ile
 * layout + theme alınır, tick stream + hot-reload event'leri dinlenir.
 *
 * Token yoksa: dev kolaylığı için mock mode'a düşer (production'da /pair).
 */
async function bootLiveMode(): Promise<void> {
    const token = localStorage.getItem('fp.device.token');
    if (!token) {
        console.warn('[pwa] device token yok — eşleştirme sayfasına yönlendiriliyor (dev: ?mock=1)');
        await bootPairMode(app!);
        return;
    }

    // Sentry device context — JWT payload'undan tenant_id/device_id (#43)
    try {
        const payload = JSON.parse(atob(token.split('.')[1] ?? '')) as {
            tenant_id?: string;
            device_id?: string;
        };
        setDeviceContext({ tenantId: payload.tenant_id, deviceId: payload.device_id });
    } catch {
        // bozuk token — context atla, GatewayClient zaten auth_error verir
    }

    // Veri-durumu bandı yalnız canlı modda (boş/önbellek/kritik uyarıları).
    mountStalenessBanner();

    // 1) Stale render — Dexie'de cached layout/theme varsa Gateway gelmeden ekrana bas
    try {
        const { loadCachedState, loadCachedTicks } = await import('./storage/db.js');
        const cached = await loadCachedState();
        if (cached) {
            applyTheme(cached.theme);
            renderLayout(cached.layout, app!);
            markLayoutReady(); // önbellekten basıldı — banner "Önbellekten gösteriliyor" der (K3)
            const ticks = await loadCachedTicks();
            for (const t of ticks) {
                updateTick(t.symbol, { symbol: t.symbol, bid: t.bid, ask: t.ask, server_ts: t.server_ts });
            }
            console.info('[pwa] stale render — cached layout +', ticks.length, 'tick');
        }
    } catch (err) {
        console.warn('[pwa] cache stale render başarısız', err);
    }

    // 2) Gateway bağlan — initial_state ile taze render + hot-reload
    // Eşleştirmede dönen gateway_url öncelikli; yoksa build-time env.
    const gatewayUrl = localStorage.getItem('fp.gateway.url')
        ?? (import.meta.env.VITE_GATEWAY_URL as string | undefined)
        ?? 'ws://127.0.0.1:4000';
    const { GatewayClient } = await import('./gateway/client.js');
    const client = new GatewayClient(gatewayUrl, token, app!);

    // Dayanıklılık katmanı — donma tespiti + bellek bütçesi (#34)
    startWatchdog();
    startMemoryMonitor();

    // OTA güncelleme yöneticisi (#35) — akıllı SW aktivasyonu
    const { setupOta } = await import('./ota/update-manager.js');
    setupOta();

    // Kiosk modu (#36) — fullscreen + wake lock + anti burn-in + key lock
    const { enterKioskMode } = await import('./kiosk/kiosk-mode.js');
    void enterKioskMode();

    // Log batcher (#38) — client hata/log batch -> device_logs
    startLogBatcher();

    (window as unknown as { __fpClient: unknown }).__fpClient = client;
    breadcrumb('boot', 'live mode aktif', { gatewayUrl });
    console.info('[pwa] live mode — GatewayClient + watchdog + OTA + kiosk + telemetri aktif');
}

/**
 * Geriye dönük: ?classic=1 ile eski single-tile preview.
 */
async function bootClassicTile(): Promise<void> {
    // Socket service'i lazy yükle — Socket.io auto-init sadece classic mode'da
    const { onTick } = await import('./services/socket.js');
    type TickWithRx = Parameters<Parameters<typeof onTick>[0]>[0];

    await import('./components/price-tile.js');
    await import('./components/latency-overlay.js');
    const FOCUS_SYMBOL = (import.meta.env.VITE_FOCUS_SYMBOL as string | undefined) ?? 'XAU/TRY:GRAM';
    app!.innerHTML = `
        <div class="flex flex-col items-center justify-center w-full h-full">
            <fp-price-tile id="tile"></fp-price-tile>
        </div>
        <fp-latency-overlay id="lat"></fp-latency-overlay>
    `;
    const tile = document.getElementById('tile') as HTMLElement & { tick: TickWithRx };
    const lat = document.getElementById('lat') as HTMLElement & { tick: TickWithRx };
    onTick((tick: TickWithRx) => {
        if (tick.symbol !== FOCUS_SYMBOL) return;
        tile.tick = tick;
        lat.tick = tick;
    });
}

async function fetchExample<T>(path: string): Promise<T> {
    const res = await fetch(path);
    if (!res.ok) throw new Error(`Failed to load ${path}: ${res.status}`);
    return (await res.json()) as T;
}
